import { useEffect, useId, useState } from "react";
import { Button, Form } from "react-bootstrap";
import axios from "../axios";
import { AdminAddButton, AdminFormActions, AdminFormModal, RecordActions } from "./AdminForm";
import { removePrompt, useConfirm } from "./ConfirmDialog";
import Message from "./Message";

function authConfig(token) {
  return { headers: { Authorization: `Bearer ${token}` } };
}

function errorText(error) {
  return error.response && error.response.data.detail ? error.response.data.detail : error.message;
}

function Section({ title, open, onToggle, children }) {
  return (
    <section className={`home-admin-card${open ? " is-open" : ""}`}>
      <h3>
        <button type="button" aria-expanded={open} onClick={onToggle}>
          {title}
          <span className="home-admin-chevron" aria-hidden="true" />
        </button>
      </h3>
      <div className="accordion-fold">
        <div className="accordion-panel" inert={!open}>
          <div className="home-admin-panel">{children}</div>
        </div>
      </div>
    </section>
  );
}

function TextField({ label, value, onChange, rows }) {
  const id = useId();
  return (
    <Form.Group className="mb-3" controlId={id}>
      <Form.Label>{label}</Form.Label>
      <Form.Control as={rows ? "textarea" : "input"} rows={rows} value={value || ""} onChange={(event) => onChange(event.target.value)} />
    </Form.Group>
  );
}

const emptyPerson = { name: "", role: "", email: "", extra_email: "" };

function PersonFields({ person, onChange, onSave, onCancel, saveLabel, busy }) {
  return (
    <Form
      className="company-admin-edit-form"
      onSubmit={(event) => {
        event.preventDefault();
        onSave();
      }}
    >
      <TextField label="Name" value={person.name} onChange={(value) => onChange("name", value)} />
      <TextField label="Role" value={person.role} onChange={(value) => onChange("role", value)} />
      <TextField label="Email" value={person.email} onChange={(value) => onChange("email", value)} />
      <TextField label="Second email" value={person.extra_email} onChange={(value) => onChange("extra_email", value)} />
      <div className="company-admin-edit-actions">
        <Button type="submit" variant="primary" disabled={busy}>
          {busy ? "Saving" : saveLabel}
        </Button>
        <button type="button" className="company-admin-cancel" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </Form>
  );
}

function AdminCompany({ token }) {
  const [page, setPage] = useState(null);
  const [openSection, setOpenSection] = useState("volunteers");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [files, setFiles] = useState([]);
  const [fileKey, setFileKey] = useState(0);
  const [person, setPerson] = useState(emptyPerson);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [ask, confirmDialog] = useConfirm();

  useEffect(() => {
    axios
      .get("/api/admin/company/", authConfig(token))
      .then(({ data }) => setPage(data))
      .catch((err) => setError(errorText(err)));
  }, [token]);

  const toggle = (id) => setOpenSection((current) => (current === id ? "" : id));

  const setPart = (section, field, value) => {
    setPage((current) => ({ ...current, [section]: { ...current[section], [field]: value } }));
  };

  const setListItem = (section, field, index, value) => {
    setPage((current) => {
      const next = [...current[section][field]];
      next[index] = value;
      return { ...current, [section]: { ...current[section], [field]: next } };
    });
  };

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const { data } = await axios.put("/api/admin/company/", page, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      setPage(data);
      setNotice("About Us saved.");
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const upload = async (event) => {
    event.preventDefault();
    if (!files.length) {
      return;
    }
    setBusy(true);
    setError("");
    setNotice("");
    const formData = new FormData();
    files.forEach((file) => formData.append("photos", file));
    try {
      const { data } = await axios.post("/api/admin/company-photos/", formData, authConfig(token));
      setPage(data);
      setFiles([]);
      setFileKey((value) => value + 1);
      setPhotoOpen(false);
      setNotice(data.detail || "Photos added.");
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const removePhoto = async (photo) => {
    if (!(await ask(removePrompt("this library photo")))) {
      return;
    }
    setError("");
    setNotice("");
    try {
      const { data } = await axios.delete(`/api/admin/company-photos/${photo.id}/`, authConfig(token));
      setPage(data);
    } catch (err) {
      setError(errorText(err));
    }
  };

  const addPerson = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const { data } = await axios.post("/api/admin/committee/", person, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      setPage(data);
      setPerson(emptyPerson);
      setAdding(false);
      setNotice("Person added.");
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const savePerson = async (member) => {
    setError("");
    setNotice("");
    try {
      const { data } = await axios.patch(`/api/admin/committee/${member.id}/`, member, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      setPage(data);
      setEditingId(null);
      setDraft(null);
      setNotice("Person saved.");
    } catch (err) {
      setError(errorText(err));
    }
  };

  const removePerson = async (member) => {
    if (!(await ask(removePrompt(member.name)))) {
      return;
    }
    setError("");
    setNotice("");
    try {
      const { data } = await axios.delete(`/api/admin/committee/${member.id}/`, authConfig(token));
      setPage(data);
      if (editingId === member.id) {
        setEditingId(null);
        setDraft(null);
      }
    } catch (err) {
      setError(errorText(err));
    }
  };

  const beginAdd = () => {
    setEditingId(null);
    setDraft(null);
    setPerson(emptyPerson);
    setAdding(true);
  };

  const beginEdit = (member) => {
    setAdding(false);
    setEditingId(member.id);
    setDraft({ ...member });
  };

  if (!page) {
    return (
      <>
        <h2>About Us</h2>
        {error ? <Message variant="danger">{error}</Message> : <p className="text-muted">Loading the page.</p>}
      </>
    );
  }

  const volunteers = page.volunteers;
  const purpose = page.purpose;
  const history = page.history;
  const committee = page.committee;

  return (
    <>
      {confirmDialog}
      <h2>About Us</h2>
      <p className="text-muted">
        These fields are the words on the public About Us tabs. Saving one tab saves the text for all four. People and
        library photos save on their own.
      </p>
      {error ? <Message variant="danger">{error}</Message> : null}
      {notice ? <Message variant="info">{notice}</Message> : null}
      <Section title="Volunteers" open={openSection === "volunteers"} onToggle={() => toggle("volunteers")}>
        <Form onSubmit={save}>
          <TextField label="Small label" value={volunteers.kicker} onChange={(value) => setPart("volunteers", "kicker", value)} />
          <TextField label="Title" value={volunteers.title} onChange={(value) => setPart("volunteers", "title", value)} />
          <TextField label="Opening" value={volunteers.lead} rows={4} onChange={(value) => setPart("volunteers", "lead", value)} />
          {volunteers.notes.map((note, index) => (
            <TextField
              key={index}
              label={`Note ${index + 1}`}
              value={note}
              rows={4}
              onChange={(value) => setListItem("volunteers", "notes", index, value)}
            />
          ))}
          <TextField label="Phone" value={volunteers.phone} onChange={(value) => setPart("volunteers", "phone", value)} />
          <TextField label="Email" value={volunteers.email} onChange={(value) => setPart("volunteers", "email", value)} />
          <TextField label="Footer heading" value={volunteers.charityTitle} onChange={(value) => setPart("volunteers", "charityTitle", value)} />
          <TextField label="Footer sentence" value={volunteers.charityBody} rows={3} onChange={(value) => setPart("volunteers", "charityBody", value)} />
          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? "Saving" : "Save text"}
          </Button>
        </Form>
      </Section>
      <Section title="Purpose" open={openSection === "purpose"} onToggle={() => toggle("purpose")}>
        <Form onSubmit={save}>
          <TextField label="Small label" value={purpose.kicker} onChange={(value) => setPart("purpose", "kicker", value)} />
          <TextField label="Title" value={purpose.title} onChange={(value) => setPart("purpose", "title", value)} />
          <TextField label="Opening" value={purpose.lead} rows={4} onChange={(value) => setPart("purpose", "lead", value)} />
          <TextField label="Left note" value={purpose.left} rows={4} onChange={(value) => setPart("purpose", "left", value)} />
          <TextField label="Right note" value={purpose.right} rows={4} onChange={(value) => setPart("purpose", "right", value)} />
          <TextField label="Weekend heading" value={purpose.weekendHeading} onChange={(value) => setPart("purpose", "weekendHeading", value)} />
          <TextField label="Weekend opening" value={purpose.weekendLead} rows={4} onChange={(value) => setPart("purpose", "weekendLead", value)} />
          <TextField label="Saturday" value={purpose.saturday} rows={3} onChange={(value) => setPart("purpose", "saturday", value)} />
          <TextField label="Sunday" value={purpose.sunday} rows={3} onChange={(value) => setPart("purpose", "sunday", value)} />
          <TextField label="Family line" value={purpose.family} rows={3} onChange={(value) => setPart("purpose", "family", value)} />
          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? "Saving" : "Save text"}
          </Button>
        </Form>
        <AdminAddButton onClick={() => setPhotoOpen(true)}>Add photos</AdminAddButton>
        <AdminFormModal show={photoOpen} title="Add library photos" onHide={() => setPhotoOpen(false)}>
        <Form onSubmit={upload}>
          <Form.Group className="mb-3" controlId="libraryPhotos">
            <Form.Label>Library photos</Form.Label>
            <Form.Control
              key={fileKey}
              type="file"
              accept="image/jpeg,image/png,.jpg,.jpeg,.png"
              multiple
              onChange={(event) => setFiles(Array.from(event.target.files || []))}
            />
          </Form.Group>
          <AdminFormActions
            saveLabel={busy ? "Uploading" : "Add photos"}
            busy={busy}
            disabled={files.length === 0}
            onCancel={() => setPhotoOpen(false)}
          />
        </Form>
        </AdminFormModal>
        {purpose.photos.length === 0 ? (
          <p className="mb-0 mt-3">No library photos yet.</p>
        ) : (
          <div className="admin-photo-grid">
            {purpose.photos.map((photo) => (
              <article key={photo.id} className="admin-photo-card">
                <img src={photo.thumb_url} alt="Library" />
                <RecordActions name="this library photo" onRemove={() => removePhoto(photo)} />
              </article>
            ))}
          </div>
        )}
      </Section>
      <Section title="History" open={openSection === "history"} onToggle={() => toggle("history")}>
        <Form onSubmit={save}>
          <TextField label="Small label" value={history.kicker} onChange={(value) => setPart("history", "kicker", value)} />
          <TextField label="Title" value={history.title} onChange={(value) => setPart("history", "title", value)} />
          <TextField label="Date line" value={history.date} rows={3} onChange={(value) => setPart("history", "date", value)} />
          <TextField label="First heading" value={history.whyHeading} onChange={(value) => setPart("history", "whyHeading", value)} />
          <TextField label="First opening" value={history.whyLead} rows={4} onChange={(value) => setPart("history", "whyLead", value)} />
          {history.whyNotes.map((note, index) => (
            <TextField
              key={index}
              label={`Story note ${index + 1}`}
              value={note}
              rows={4}
              onChange={(value) => setListItem("history", "whyNotes", index, value)}
            />
          ))}
          <TextField label="Closing story" value={history.whyClose} rows={4} onChange={(value) => setPart("history", "whyClose", value)} />
          <TextField label="Years heading" value={history.yearsHeading} onChange={(value) => setPart("history", "yearsHeading", value)} />
          <TextField label="First year" value={history.yearsLeft} rows={4} onChange={(value) => setPart("history", "yearsLeft", value)} />
          <TextField label="Park story" value={history.yearsRight} rows={4} onChange={(value) => setPart("history", "yearsRight", value)} />
          <TextField label="Automobile label" value={history.autosLabel} onChange={(value) => setPart("history", "autosLabel", value)} />
          <TextField
            label="Automobiles, one name on each line"
            value={(history.automobiles || []).join("\n")}
            rows={8}
            onChange={(value) => setPart("history", "automobiles", value.split("\n"))}
          />
          <TextField label="Motorcycle label" value={history.motosLabel} onChange={(value) => setPart("history", "motosLabel", value)} />
          <TextField
            label="Motorcycles, one name on each line"
            value={(history.motorcycles || []).join("\n")}
            rows={8}
            onChange={(value) => setPart("history", "motorcycles", value.split("\n"))}
          />
          {history.notes.map((note, index) => (
            <TextField
              key={index}
              label={`Closing note ${index + 1}`}
              value={note}
              rows={2}
              onChange={(value) => setListItem("history", "notes", index, value)}
            />
          ))}
          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? "Saving" : "Save text"}
          </Button>
        </Form>
      </Section>
      <Section title="Committee" open={openSection === "committee"} onToggle={() => toggle("committee")}>
        <Form className="company-admin-heading" onSubmit={save}>
          <TextField label="Small label" value={committee.kicker} onChange={(value) => setPart("committee", "kicker", value)} />
          <TextField label="Title" value={committee.title} onChange={(value) => setPart("committee", "title", value)} />
          <Button className="company-admin-heading-save" type="submit" variant="primary" disabled={busy}>
            {busy ? "Saving" : "Save heading"}
          </Button>
        </Form>
        <AdminAddButton onClick={beginAdd}>Add a committee member</AdminAddButton>
        <AdminFormModal
          show={adding || Boolean(editingId)}
          title={editingId ? `Edit ${draft?.name || "committee member"}` : "Add a committee member"}
          onHide={() => {
            setAdding(false);
            setEditingId(null);
            setDraft(null);
          }}
        >
          {adding ? (
            <PersonFields
              person={person}
              onChange={(field, value) => setPerson((current) => ({ ...current, [field]: value }))}
              onSave={() => addPerson({ preventDefault() {} })}
              onCancel={() => setAdding(false)}
              saveLabel="Add member"
              busy={busy}
            />
          ) : draft ? (
            <PersonFields
              person={draft}
              onChange={(field, value) => setDraft((current) => ({ ...current, [field]: value }))}
              onSave={() => savePerson(draft)}
              onCancel={() => {
                setEditingId(null);
                setDraft(null);
              }}
              saveLabel="Save person"
              busy={busy}
            />
          ) : null}
        </AdminFormModal>
        <ul className="company-admin-list">
          {committee.members.map((member) => (
            <li key={member.id} className="company-admin-row">
              <strong>{member.name}</strong>
              <span>{member.role}</span>
              <span>{[member.email, member.extra_email].filter(Boolean).join(" · ") || "No email yet"}</span>
              <RecordActions
                name={member.name}
                onEdit={() => beginEdit(member)}
                onRemove={() => removePerson(member)}
              />
            </li>
          ))}
        </ul>
      </Section>
    </>
  );
}

export default AdminCompany;
